import {
  Accordion,
  AccordionItem,
  Cart,
  CartItem,
  Divider,
  MobileCart,
  useTheme,
} from '@vfs-digital-channels/ns-react-components';
import React, { useEffect } from 'react';
import {
  calculateCartTotal,
  calculateTotalCartQuantity,
  filterCartItemsByPriceType,
  PaymentMethodCode,
} from '@vfs-digital-channels/ns-utils';
import { createPortal } from 'react-dom';
import {
  AcquiringChannel,
  Application,
  ApplicationActionType,
  CatalogueItem,
  formatCurrencyAmountRands,
  PriceType,
} from 'src/features/common';
import type { CartItem as Item } from 'src/features/common/types/application/cart';
import { useApplication } from 'src/hooks/useApplication';
import { getProductName, getProductNamesInCart } from '../../helpers/tealium-service-helpers';
import { Environment } from 'src/Environment';
import { VodapayLogoV2 } from 'src/assets/images';
import { Toast } from 'src/features/common/components/toast/toast';

export type ShoppingCartProps = {
  automationid?: string;
  onBasketOpened?: (isBasketOpen: boolean) => void;
  highlightProduct?: string;
  displayedProduct?: string;
  application: Application;
  onCheckout: () => void;
  isCartOpen: boolean;
  setIsCartOpen: React.Dispatch<React.SetStateAction<boolean>>;
  disabled?: boolean;
  isQuickEdit?: boolean;
  onContinueShopping?: () => void;
};

function TotalItem(props: { label: string; total: string }) {
  const { theme } = useTheme();

  const isRevamp = theme === 'revamp';

  return (
    <section
      className={`flex items-center justify-between ${
        isRevamp ? 'mb-4' : 'mt-4'
      } text-base font-normal leading-6 text-black`}
    >
      <p>{props.label}</p>
      <p className="font-bold uppercase">{props.total}</p>
    </section>
  );
}

function Device({
  device,
  highlightProduct,
  displayedProduct,
}: {
  device: Item;
  highlightProduct?: string;
  displayedProduct?: string;
}) {
  const { dispatch, catalogue, application, dpoTealiumService } = useApplication();

  let price = '';
  let isRented = false;
  let totalPrice = 0;
  let priceSuffix = '';

  switch (device.priceType) {
    case PriceType.ONCE_OFF:
      totalPrice = (device.price ?? 0) * device.quantity;
      price = formatCurrencyAmountRands(totalPrice, true);
      priceSuffix = 'Once off';
      break;
    case PriceType.MONTHLY:
      totalPrice = (device.price ?? 0) * device.quantity;
      price = formatCurrencyAmountRands(totalPrice, true);
      priceSuffix = 'Per month';
      isRented = device.priceType === PriceType.MONTHLY;
      break;
  }

  const updateQuantity = (
    productName: string,
    quantity: number,
    cartItemId: string,
    application: Application,
    paymentMethodCode?: PaymentMethodCode | null,
  ) => {
    const product: CatalogueItem | undefined = catalogue.products.find(
      (product) => product.device.title === productName,
    );

    if (!product) {
      return;
    }

    const cartItem = application.cart?.find((item) => item.id === cartItemId);

    dispatch({
      type: ApplicationActionType.UPDATE_CART_QUANTITY,
      productId: product.device.id,
      acquiringChannel: cartItem?.acquiringChannel || product.device.acquiringChannel,
      quantity: quantity,
      catalogue: catalogue,
      cartItemId: cartItemId,
      paymentMethodCode: cartItem?.paymentMethodCode || paymentMethodCode,
    });
  };

  const removeItemFromCart = (
    productId: string,
    acquiringChannel: AcquiringChannel,
    cartItemId: string,
  ) => {
    dispatch({
      type: ApplicationActionType.REMOVE_CART_QUANTITY,
      productId: productId,
      acquiringChannel: acquiringChannel,
      catalogue: catalogue,
      cartItemId: cartItemId,
    });
  };

  return (
    <CartItem
      maxQuantity={70}
      minQuantity={0}
      image={device.image}
      description={device.tagline}
      name={device.title}
      quantity={device.quantity}
      disable={device.isVirtual}
      price={price}
      priceType={device.priceType}
      priceSuffix={priceSuffix}
      inputName="cart.products[0].device.quantity"
      key={device.productId}
      productId={device.productId}
      onRemove={() => {
        removeItemFromCart(device.productId, device.acquiringChannel, device.id);

        dpoTealiumService?.removesProductsFromCartChooseSolution(
          displayedProduct!,
          application.cart && application.cart.length > 0
            ? application.cart.map((item: Item) => getProductName(item.title))
            : [highlightProduct!].map((name) => getProductName(name)),
          getProductName(device.title),
        );
      }}
      onChangeQuantity={(quantity) => {
        updateQuantity(
          device.title,
          quantity,
          device.id || '',
          application,
          device.paymentMethodCode,
        );

        // add to cart
        if (device.quantity < quantity) {
          dpoTealiumService?.changesProductQuantityInCart(
            displayedProduct!,
            getProductNamesInCart(application.cart || []),
            getProductName(device.title),
          );
        }
      }}
      showNote={isRented}
    />
  );
}

function isMixedCart(cart: Item[]): boolean {
  const priceTypes = cart.map((item) => item.priceType);
  return priceTypes.some((priceType) => priceTypes[0] !== priceType);
}

export function ShoppingCart(props: ShoppingCartProps) {
  const numberOfDevices = calculateTotalCartQuantity(props.application.cart || []);
  const footerButton = React.useMemo(
    (): HTMLElement | null => document.getElementById('mobile-footer-secondary-button'),
    [],
  );

  const revampEnabled = React.useMemo(
    (): boolean => !Environment.current.disabledFeatures.revamp,
    [],
  );

  const rentalDevices = filterCartItemsByPriceType(props.application.cart || [], PriceType.MONTHLY);
  const onceOffDevices = filterCartItemsByPriceType(
    props.application.cart || [],
    PriceType.ONCE_OFF,
  );
  const freeDevices = filterCartItemsByPriceType(props.application.cart || [], PriceType.FREE);

  const rentalDevicesTotal = calculateCartTotal(rentalDevices, true);

  const onceOffDevicesTotal = calculateCartTotal(onceOffDevices);
  const hasRentalDevices = rentalDevices.length > 0;
  const hasOnceOffDevices = onceOffDevices.length > 0;
  const hasFreeDevices = freeDevices.length > 0;
  const isRentalOnly = hasRentalDevices && !hasOnceOffDevices && !hasFreeDevices;
  const mixedCart = isMixedCart(props.application.cart || []);

  const defaultValue = hasRentalDevices ? ['rental'] : hasOnceOffDevices ? ['onceOff'] : ['free'];

  useEffect(() => {
    props.onBasketOpened && props.onBasketOpened(props.isCartOpen);
  }, [props.isCartOpen]);

  useEffect(() => {
    if (props.application.cart && props.application.cart.length === 0) props.setIsCartOpen(false);
  }, [props.application.cart]);

  return (
    <article className="">
      <Toast />
      {(numberOfDevices > 0 || revampEnabled) && (
        <Cart
          checkoutLabel={props.isQuickEdit ? 'Update' : 'Proceed to checkout'}
          header="Your Cart"
          quantity={numberOfDevices}
          logoSrc={VodapayLogoV2}
          isRentalOnly={isRentalOnly}
          isMixedCart={mixedCart}
          open={props.isCartOpen}
          onCheckout={() => {
            props.setIsCartOpen(false);
            props.onCheckout();
          }}
          onContinueShopping={props.onContinueShopping}
          checkoutDisabled={props.disabled}
          onClose={() => props.setIsCartOpen(!props.isCartOpen)}
          cartTotal={
            onceOffDevicesTotal
              ? formatCurrencyAmountRands(onceOffDevicesTotal, true)
              : rentalDevicesTotal && isRentalOnly
              ? `${formatCurrencyAmountRands(rentalDevicesTotal, true)} PM`
              : ''
          }
          className="overflow-y-auto"
        >
          <Accordion
            key={defaultValue?.[0]}
            type="multiple"
            className="[&_.ns\-border\-b]:border-0 [&_.trigger]:py-0 [&_.trigger]:font-semibold"
            defaultValue={['rental', 'onceOff', 'free']}
          >
            {hasRentalDevices && (
              <>
                {!revampEnabled && <Divider />}
                <AccordionItem
                  className="py-2 border-1 border-grey-200 rounded-16 md:py-3"
                  value="rental"
                  title="Rent"
                >
                  <section className="py-2 pt-4 mt-4 space-y-4 border-2 ns-px-3 md:ns-px-6 border-grey-200 rounded-16 md:py-3">
                    {rentalDevices.map((device) => (
                      <Device
                        key={device.productId}
                        device={device}
                        highlightProduct={props.highlightProduct}
                        displayedProduct={props.displayedProduct}
                      />
                    ))}
                  </section>
                </AccordionItem>
                <TotalItem
                  label="Total monthly payments"
                  total={formatCurrencyAmountRands(rentalDevicesTotal, true)}
                />
              </>
            )}
            {hasOnceOffDevices && (
              <>
                {!revampEnabled && <Divider />}
                <AccordionItem value="onceOff" title="Buy">
                  <section className="py-2 pt-4 mt-4 space-y-4 border-2 ns-px-3 md:ns-px-6 border-grey-200 rounded-16 md:py-3">
                    {onceOffDevices.map((device) => (
                      <Device
                        key={device.productId}
                        device={device}
                        highlightProduct={props.highlightProduct}
                        displayedProduct={props.displayedProduct}
                      />
                    ))}
                  </section>
                </AccordionItem>
                <TotalItem
                  label="Total once-off payments"
                  total={formatCurrencyAmountRands(onceOffDevicesTotal, true)}
                />
              </>
            )}
            {hasFreeDevices && (
              <>
                {!revampEnabled && <Divider />}
                <AccordionItem value="free" title="Free">
                  <section className="py-2 pt-4 mt-4 space-y-4 border-2 ns-px-3 md:ns-px-6 border-grey-200 rounded-16 md:py-3">
                    {freeDevices.map((device) => (
                      <Device
                        key={device.productId}
                        device={device}
                        highlightProduct={props.highlightProduct}
                        displayedProduct={props.displayedProduct}
                      />
                    ))}
                  </section>
                </AccordionItem>
              </>
            )}
            {!revampEnabled && <Divider />}
          </Accordion>
        </Cart>
      )}
      {footerButton &&
        createPortal(
          <MobileCart
            quantity={numberOfDevices}
            onClick={() => props.setIsCartOpen(!props.isCartOpen)}
          />,
          footerButton,
        )}
    </article>
  );
}
